import React, { useState } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  OnChangeFn,
} from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { Search, ChevronLeft, ChevronRight, Loader2, Filter, ChevronDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { JobCardListItem } from '@/app/types/jobCard';
import { useDebounce } from '@/hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Define a type for the custom meta properties
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
  enableColumnFilter?: boolean;
};

interface JobCardDataTableProps {
  data: JobCardListItem[];
  columns: ExtendedColumnDef<JobCardListItem>[];
  isLoading: boolean;
  pageCount?: number;
  page: number;
  setPage: (page: number) => void;
  onSearchChange: (value: string) => void;
  searchValue: string;
  onSortingChange?: (sorting: SortingState) => void;
  sortingState?: SortingState;
  onFilterChange?: (
    filters: Record<string, string | number | boolean | { from?: string; to?: string }>
  ) => void;
}

export function JobCardDataTable({
  data,
  columns,
  isLoading,
  pageCount = 0,
  page,
  setPage,
  onSearchChange,
  searchValue,
  onSortingChange,
  sortingState = [],
  onFilterChange,
}: JobCardDataTableProps): React.ReactElement {
  const [sorting, setSorting] = useState<SortingState>(sortingState);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    job_card_type: false,
    bay_number: false,
  });
  const [rowSelection, setRowSelection] = useState({});
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState(searchValue);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const debouncedSearch = useDebounce(searchInput, 500);

  React.useEffect(() => {
    onSearchChange(debouncedSearch);
  }, [debouncedSearch, onSearchChange]);

  React.useEffect(() => {
    if (onFilterChange) {
      const apiFilters: Record<
        string,
        string | number | boolean | { from?: string; to?: string }
      > = {};

      columnFilters.forEach((filter) => {
        const value = filter.value;
        if (value !== undefined && value !== null && value !== '') {
          if (typeof value === 'object' && 'from' in value) {
            const dateRange = value as { from?: string; to?: string };
            if (dateRange.from || dateRange.to) {
              apiFilters[filter.id] = dateRange;
            }
          } else if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
          ) {
            apiFilters[filter.id] = value;
          }
        }
      });

      const timeoutId = setTimeout(() => {
        onFilterChange(apiFilters);
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [columnFilters, onFilterChange]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = updater instanceof Function ? updater(sorting) : updater;
    setSorting(newSorting);
    if (onSortingChange) {
      onSortingChange(newSorting);
    }
    setPage(0);
  };

  const handleColumnFiltersChange = (
    updater: ColumnFiltersState | ((state: ColumnFiltersState) => ColumnFiltersState)
  ): void => {
    const newFilters = updater instanceof Function ? updater(columnFilters) : updater;
    setColumnFilters(newFilters);
    setPage(0);
  };

  const table = useReactTable({
    data,
    columns: columns as ColumnDef<JobCardListItem, unknown>[],
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: pageCount,
  });

  const handleRowClick = (e: React.MouseEvent, id: number): void => {
    const path = `/workshop/job-cards/detail/${id}`;

    if ((e.ctrlKey || e.metaKey) && window.api?.tabs) {
      e.preventDefault();
      window.api.tabs.create(path, `Job Card #${id}`);
    } else {
      navigate(path);
    }
  };

  const renderPaginationControls = (): JSX.Element => (
    <div className="flex items-center justify-between w-full">
      <div className="text-xs sm:text-sm text-muted-foreground">
        Page {page + 1} of {pageCount || 1}
      </div>
      <div className="flex items-center space-x-1 sm:space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page - 1)}
          disabled={page === 0 || isLoading}
          className="h-9 sm:h-8 touch-manipulation"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Previous</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page + 1)}
          disabled={page + 1 >= pageCount || isLoading}
          className="h-9 sm:h-8 touch-manipulation"
        >
          <span className="hidden sm:inline mr-1">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const getColumnKey = (column: ExtendedColumnDef<JobCardListItem, unknown>): string => {
    return column.id ?? (column as { accessorKey?: string }).accessorKey ?? '';
  };

  const renderFilterControl = (
    column: ExtendedColumnDef<JobCardListItem, unknown>
  ): JSX.Element | null => {
    const columnKey = getColumnKey(column);
    const tableColumn = table.getColumn(columnKey);
    const columnMeta = column.meta;

    if (!tableColumn || !column.enableColumnFilter || !columnMeta?.variant) {
      return null;
    }

    const filterValue = tableColumn.getFilterValue();

    if (columnMeta.variant === 'text' || columnMeta.variant === 'number') {
      return (
        <Input
          type={columnMeta.variant === 'number' ? 'number' : 'text'}
          value={(filterValue ?? '') as string}
          onChange={(e) => tableColumn.setFilterValue(e.target.value)}
          placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`}
          className="h-8 w-full max-w-xs"
        />
      );
    }

    if (columnMeta.variant === 'select' && columnMeta.options) {
      const ALL_VALUE = '__ALL__';
      return (
        <Select
          value={(filterValue ?? ALL_VALUE) as string}
          onValueChange={(value) =>
            tableColumn.setFilterValue(value === ALL_VALUE ? undefined : value)
          }
        >
          <SelectTrigger className="h-8 w-full max-w-xs">
            <SelectValue
              placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All</SelectItem>
            {columnMeta.options.map((option) => (
              <SelectItem key={String(option.value)} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (columnMeta.variant === 'dateRange') {
      const filterVal = filterValue as { from?: string; to?: string } | undefined;
      const dateRange: DateRange | undefined = filterVal
        ? {
            from: filterVal.from ? new Date(filterVal.from) : undefined,
            to: filterVal.to ? new Date(filterVal.to) : undefined,
          }
        : undefined;

      return (
        <DateRangePicker
          date={dateRange}
          onDateChange={(newDate) => {
            if (newDate?.from || newDate?.to) {
              const newFilterVal = {
                from: newDate.from ? format(newDate.from, 'yyyy-MM-dd') : undefined,
                to: newDate.to ? format(newDate.to, 'yyyy-MM-dd') : undefined,
              };
              tableColumn.setFilterValue(newFilterVal);
            } else {
              tableColumn.setFilterValue(undefined);
            }
          }}
          className="h-8 w-[300px]"
        />
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Global Search & Controls */}
      <div className="flex items-center gap-2 sm:gap-4">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search job cards..."
            value={searchInput || ''}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8 w-full h-10 sm:h-9"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-10 sm:h-9 sm:hidden shrink-0"
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <Filter className="h-4 w-4 mr-1" />
          Filters
          <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-10 sm:h-9 hidden sm:inline-flex">
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {columns.map((column) => {
              const columnKey = getColumnKey(column);
              const tableCol = table.getColumn(columnKey);
              return (
                tableCol?.getCanHide() && (
                  <DropdownMenuCheckboxItem
                    key={columnKey}
                    className="capitalize"
                    checked={tableCol.getIsVisible()}
                    onCheckedChange={(value) => tableCol.toggleVisibility(!!value)}
                  >
                    {column.meta?.label || columnKey}
                  </DropdownMenuCheckboxItem>
                )
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Column Filters — collapsible on mobile */}
      <div className={`${filtersOpen ? 'flex' : 'hidden'} sm:flex items-start gap-2 flex-wrap p-2 border rounded-md bg-card`}>
        {columns.map(
          (column) =>
            column.enableColumnFilter && (
              <div key={getColumnKey(column)} className="flex flex-col space-y-1 flex-1 min-w-[140px] sm:min-w-0 sm:flex-none">
                <label className="text-xs font-medium text-muted-foreground">
                  {column.meta?.label || getColumnKey(column)}
                </label>
                {renderFilterControl(column)}
              </div>
            )
        )}
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table className="min-w-[640px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex items-center justify-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={(e) => handleRowClick(e, row.original.id)}
                  className="cursor-pointer hover:bg-muted"
                  title="Click to view, Ctrl/Cmd+Click to open in new tab"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No job cards found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      {renderPaginationControls()}
    </div>
  );
}
